import { useState, useMemo } from 'react'
import { CoBillFragment, CoDepositFragment, CoDocumentFragment, CoReservationFragment } from 'src/api/api.generated'
import { CreateCoParameter, UpdateCoParameter } from '../checkout-document-form'
import { useCreateCoDocumentQuery, useReservationCoDocumentQuery, useUpdateCoDocumentQuery } from '../checkout-document-form.api'

export type CoData =
  | {
      mode: 'CREATE'
      bills: CoBillFragment[]
      deposits: CoDepositFragment[]
      reservations: CoReservationFragment[]
    }
  | {
      mode: 'UPDATE'
      document: CoDocumentFragment
      reservations: CoReservationFragment[]
    }
  | {
      mode: 'READ_ONLY'
      document: CoDocumentFragment
      reservations: CoReservationFragment[]
    }

export type CoLoadingError =
  | {
      mode: 'ERROR'
    }
  | {
      mode: 'LOADING'
    }
export type CoQuery = CoData | CoLoadingError

export type CoQueryProps = {
  propertyId: string
  children: (data: CoQuery, onDocumentUpserted: (documentId: string) => void) => React.ReactNode
} & (CreateCoParameter | UpdateCoParameter)

export function CoQueryWrapper(props: CoQueryProps) {
  const propertyId = props.propertyId
  const [documentId, setDocumentId] = useState('documentId' in props ? props.documentId : '')

  const skipReservation = !('reservationId' in props) || !props.reservationId
  const { billIds, productionAssignedIds, depositDocumentIds, skipCreate } =
    'billIds' in props && !documentId //
      ? { ...props, skipCreate: !skipReservation }
      : { billIds: [], productionAssignedIds: [], depositDocumentIds: [], skipCreate: true }
  const skipUpdate = !skipCreate || !skipReservation

  const createData = useCreateCoDocumentQuery(propertyId, billIds, productionAssignedIds, depositDocumentIds, skipCreate)
  const updateData = useUpdateCoDocumentQuery(propertyId, documentId, skipUpdate)
  const createReceiptZeroData = useReservationCoDocumentQuery({
    reservationId: 'reservationId' in props ? props.reservationId : '',
    propertyId,
    skip: skipReservation,
  })

  const data: CoQuery = useMemo(() => {
    if (!skipReservation) {
      if (createReceiptZeroData.loading) {
        return { mode: 'LOADING' }
      }
      if (createReceiptZeroData.error) {
        return { mode: 'ERROR' }
      }

      return { mode: 'CREATE', bills: [], deposits: [], reservations: createReceiptZeroData.data?.reservations ?? [] }
    } else if (skipCreate) {
      if (updateData.loading) {
        return { mode: 'LOADING' }
      }
      if (updateData.error) {
        return { mode: 'ERROR' }
      }
      const document = updateData.data?.document
      const reservations = updateData.data?.reservations || []
      if (!document) {
        return { mode: 'ERROR' }
      }

      if (document.auditable.state === 'DRAFT') {
        return { mode: 'UPDATE', document, reservations }
      } else {
        return { mode: 'READ_ONLY', document, reservations }
      }
    } else {
      if (createData.loading) {
        return { mode: 'LOADING' }
      }
      if (createData.error) {
        return { mode: 'ERROR' }
      }
      const bills = createData.data?.bills || []
      const deposits = createData.data?.deposits || []
      const reservations = createData.data?.reservations || []

      if (bills.length <= 0) {
        return { mode: 'ERROR' }
      }
      return { mode: 'CREATE', bills, deposits, reservations }
    }
  }, [skipCreate, createData, updateData, createReceiptZeroData, skipReservation])

  return <>{props.children(data, setDocumentId)}</>
}
